import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const EMPTY_SET = new Set<string>();

interface ExportStoreState {
  /** Map of brandName -> Set of selected store POI IDs */
  selectedStoresByBrand: Record<string, Set<string>>;
  toggleStore: (brandName: string, poiId: string) => void;
  getSelectedStores: (brandName: string) => Set<string>;
  setSelectedStores: (brandName: string, ids: Set<string>) => void;
  clearBrand: (brandName: string) => void;
}

/** Stable selector that returns a frozen empty set when no stores are selected for the brand */
export const selectStoresForBrand = (brandKey: string) => (state: ExportStoreState) =>
  state.selectedStoresByBrand[brandKey] || EMPTY_SET;

export const useExportStore = create<ExportStoreState>()(
  persist(
    (set, get) => ({
      selectedStoresByBrand: {},

      toggleStore: (brandName: string, poiId: string) => {
        set((state) => {
          const current = new Set(state.selectedStoresByBrand[brandName] || []);
          if (current.has(poiId)) {
            current.delete(poiId);
          } else {
            current.add(poiId);
          }
          return {
            selectedStoresByBrand: {
              ...state.selectedStoresByBrand,
              [brandName]: current,
            },
          };
        });
      },

      getSelectedStores: (brandName: string) => {
        return get().selectedStoresByBrand[brandName] || new Set();
      },

      setSelectedStores: (brandName: string, ids: Set<string>) => {
        set((state) => ({
          selectedStoresByBrand: {
            ...state.selectedStoresByBrand,
            [brandName]: ids,
          },
        }));
      },

      clearBrand: (brandName: string) => {
        set((state) => {
          const updated = { ...state.selectedStoresByBrand };
          delete updated[brandName];
          return { selectedStoresByBrand: updated };
        });
      },
    }),
    {
      name: 'export-store',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          // Convert arrays back to Sets
          if (parsed?.state?.selectedStoresByBrand) {
            const converted: Record<string, Set<string>> = {};
            for (const [brand, ids] of Object.entries(parsed.state.selectedStoresByBrand)) {
              converted[brand] = new Set(ids as string[]);
            }
            parsed.state.selectedStoresByBrand = converted;
          }
          return parsed;
        },
        setItem: (name, value) => {
          // Convert Sets to arrays for JSON serialization
          const toSerialize = { ...value };
          if (toSerialize.state?.selectedStoresByBrand) {
            const converted: Record<string, string[]> = {};
            for (const [brand, ids] of Object.entries(toSerialize.state.selectedStoresByBrand)) {
              converted[brand] = Array.from(ids as Set<string>);
            }
            toSerialize.state = { ...toSerialize.state, selectedStoresByBrand: converted } as any;
          }
          localStorage.setItem(name, JSON.stringify(toSerialize));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
